/**
 * Frontend Enums
 *
 * This file contains ONLY frontend-specific enums that don't exist in backend.
 *
 * Backend-synced enums (GameStatus, UnitStatus, etc.) are accessed via:
 *   import { GameStatus, Team } from '@/utils/enums';
 *   // Usage: GameStatus.LOBBY, Team.OWN
 *
 * @see useMetadataStore.getEnum() for backend enum access
 * @see EnumCollectorService.php for list of backend enums
 */

import useMetadataStore from '@/store/useMetadataStore';

// =============================================================================
// BACKEND ENUM HELPERS
// Must be called AFTER metadata is loaded (post-login)
// All throw errors if not loaded (FAIL LOUDLY - no fallbacks)
// =============================================================================

/**
 * Get an enum from metadata store
 * @param {string} enumName - The enum name (e.g., 'GameStatus', 'TeamClassification')
 * @returns {object} Enum object with KEY: value pairs
 * @throws {Error} If metadata not loaded or enum not found
 */
export function getEnum(enumName) {
  return useMetadataStore.getState().getEnum(enumName);
}

/**
 * Get a specific enum value
 * @param {string} enumName - The enum name
 * @param {string} key - The enum key
 * @returns {string} The enum value
 * @throws {Error} If enum or key not found
 */
export function getEnumValue(enumName, key) {
  return useMetadataStore.getState().getEnumValue(enumName, key);
}

/**
 * Create a Proxy that lazily accesses enum values from metadata store.
 * Allows clean syntax: GameStatus.LOBBY instead of GameStatus().LOBBY
 * Throws if metadata not loaded (FAIL LOUDLY).
 *
 * @param {string} enumName - The enum name in metadata
 * @returns {Proxy} Proxy object that behaves like an enum
 */
function createEnumProxy(enumName) {
  return new Proxy({}, {
    get(_, key) {
      // Special cases for JS internals
      if (key === Symbol.toStringTag) return enumName;
      if (key === 'toString') return () => `[Enum ${enumName}]`;
      if (key === Symbol.iterator) return undefined;

      return useMetadataStore.getState().getEnumValue(enumName, key);
    },
    ownKeys() {
      try {
        return Object.keys(useMetadataStore.getState().getEnum(enumName));
      } catch {
        return [];
      }
    },
    getOwnPropertyDescriptor(_, key) {
      return {
        enumerable: true,
        configurable: true,
        value: this.get(_, key)
      };
    }
  });
}

// =============================================================================
// BACKEND-SYNCED ENUMS (via Proxy)
// Usage: GameStatus.LOBBY, Team.OWN, UnitStatus.DEPLOYED
// These read from metadata store - throws if not loaded (FAIL LOUDLY)
// =============================================================================

export const GameStatus = createEnumProxy('GameStatus');
export const UnitStatus = createEnumProxy('UnitStatus');
export const PlayerType = createEnumProxy('PlayerType');
export const PlayerStatusInGame = createEnumProxy('PlayerStatusInGame');
export const PlannedActionStatus = createEnumProxy('PlannedActionStatus');
export const EffectCategory = createEnumProxy('EffectCategory');
export const TriggerType = createEnumProxy('TriggerType');
export const DamageType = createEnumProxy('DamageType');
export const UnitStat = createEnumProxy('UnitStat');
export const Team = createEnumProxy('TeamClassification');

// =============================================================================
// FRONTEND-ONLY ENUMS
// These do NOT exist in backend - used only for UI/frontend logic
// =============================================================================

/**
 * UI feedback message status types
 * Used by feedback component for toast styling
 */
export const MessageStatus = Object.freeze({
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  LOG: 'log',
});

// =============================================================================
// CINEMATIC SYSTEM ENUMS
// =============================================================================

/**
 * Speed presets for cinematic playback
 */
export const SPEED_PRESETS = Object.freeze({
  SLOW: 0.5,
  NORMAL: 1,
  FAST: 2,
  VERY_FAST: 4
});

/**
 * Artificial action types for START/END states
 */
export const ARTIFICIAL_ACTIONS = Object.freeze({
  START: 'CINEMATIC_START',
  END: 'CINEMATIC_END'
});

/**
 * Visual effect types that can be triggered
 */
export const EFFECT_TYPES = Object.freeze({
  // Unit effects
  MOVE: 'unit_move',
  DEATH: 'unit_death',
  DAMAGE: 'unit_damage',
  HEAL: 'unit_heal',
  STATUS_APPLY: 'status_apply',
  STATUS_REMOVE: 'status_remove',

  // Combat effects
  ATTACK: 'attack',
  PROJECTILE: 'projectile',
  EXPLOSION: 'explosion',
  SHIELD: 'shield',

  // UI effects
  CAMERA_FOCUS: 'camera_focus',
  SCREEN_SHAKE: 'screen_shake',
  FLASH: 'flash'
});

/**
 * Animation states for promise tracking
 */
export const ANIMATION_STATES = Object.freeze({
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
});

// =============================================================================
// CLICK SYSTEM ENUMS
// =============================================================================

/**
 * Click contexts - define what mode the UI is in
 */
export const ClickContext = Object.freeze({
  SELECT: 'select',
  DEPLOY_UNIT: 'deploy_unit',
  TARGET_ACTION: 'target_action',
  DEBUG_KILL: 'debug_kill',
});

/**
 * Clickable types - what kind of object was clicked
 */
export const ClickableType = Object.freeze({
  UNIT: 'unit',
  DEPLOYABLE: 'deployable',
  ACTION: 'action',
  PLANNED_ACTION: 'planned_action',
  TILE: 'tile',
  TERRAIN: 'terrain',
  STRUCTURE: 'structure',
});

// =============================================================================
// DISPLAY MAPPINGS
// Frontend-only mappings for showing readable names in UI
// The actual enum values come from backend via TriggerType()
// =============================================================================

/**
 * Display name mapping for trigger types
 * Used in EventCard to show readable names in timeline
 */
export const TriggerTypeDisplay = Object.freeze({
  'on_apply': 'Applied',
  'on_turn_start': 'Turn Start',
  'on_turn_end': 'Turn End',
  'on_attacked_post': 'Counter',
  'on_attack_pre': 'Before Attack',
  'on_attack_post': 'After Attack',
  'on_attack': 'On Attack',
  'on_kill': 'On Kill',
  'on_apply_action': 'Action Used',
  'on_apply_action_post': 'After Action',
  'on_expire': 'Expired',
});

/**
 * Display name mapping for removal reasons (lifecycle events)
 * Used in EventCard to show why an effect was removed
 */
export const RemovalReasonDisplay = Object.freeze({
  'dispelled': 'Dispelled',
  'unit_death': 'Removed on Death',
  'out_of_counters': 'Duration Ended',
  'expired': 'Expired',
  'max_stack': 'Replaced by Stack',
});
